import React, { Suspense, useEffect, useMemo, useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { AiOutlineShoppingCart } from '@react-icons/all-files/ai/AiOutlineShoppingCart'
import { FaStar } from '@react-icons/all-files/fa/FaStar'
import { FiArrowRight } from '@react-icons/all-files/fi/FiArrowRight'
import { FiCalendar } from '@react-icons/all-files/fi/FiCalendar'
import { FiCheck } from '@react-icons/all-files/fi/FiCheck'
import { FiChevronLeft } from '@react-icons/all-files/fi/FiChevronLeft'
import { FiChevronRight } from '@react-icons/all-files/fi/FiChevronRight'
import { FiHome } from '@react-icons/all-files/fi/FiHome'
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle'
import { FiPackage } from '@react-icons/all-files/fi/FiPackage'
import { FiPhone } from '@react-icons/all-files/fi/FiPhone'
import { FiShield } from '@react-icons/all-files/fi/FiShield'
import { FiTruck } from '@react-icons/all-files/fi/FiTruck'
import { FiX } from '@react-icons/all-files/fi/FiX'
import { helper } from '../../../utilities/helper'
import Action from '../primitives/Action'
import Media from '../primitives/Media'
import UiBoundary from '../foundations/UiBoundary'
import ProductPortfolioProof from './ProductPortfolioProof'
import styles from './ProductDetailV2.module.scss'

const AddUnits = dynamic(() => import('../../AddUnits'), { suspense: true })

const parseSnippet = snippet => {
  const [rawLabel, ...rawValue] = String(snippet || '').split('//')
  const label = rawLabel?.trim()
  const value = rawValue.join('//').trim()
  return label && value ? { label, value } : null
}

const uniqueImages = product => [...new Set([
  product?.picture,
  ...(product?.pictures || [])
].filter(Boolean))]

const findEventCapacity = description => {
  const match = String(description || '').match(/up to\s+([\d,]+)\s+people(?:\s*\(([\d,]+)\s+if you rent a pair\))?/i)
  if (!match) return null
  return {
    single: match[1],
    pair: match[2] || null
  }
}

const relatedReason = productName => {
  const value = String(productName || '').toLowerCase()
  if (value.includes('microphone')) return 'Add a convenient audio source for speeches, announcements, or performers.'
  if (value.includes('mixer')) return 'Connect and control multiple microphones, music sources, or speakers.'
  if (value.includes('subwoofer')) return 'Add more low-frequency impact for music and dance-floor coverage.'
  if (value.includes('speaker')) return 'Expand coverage and create a more balanced sound field.'
  if (value.includes('stand') || value.includes('tripod')) return 'Complete the setup with compatible support equipment.'
  return 'Frequently selected with this product for a more complete event setup.'
}

const RatingSummary = ({ reviews = [] }) => {
  if (!reviews.length) return (
    <span className={styles.trustLine}>
      <FiShield aria-hidden='true' /> Real equipment photos · Book online
    </span>
  )

  const rating = reviews.reduce((total, review) => total + Number(review.rating || 0), 0) / reviews.length
  return (
    <a className={styles.rating} href='#product-reviews' aria-label={`${rating.toFixed(1)} out of 5 stars from ${reviews.length} reviews`}>
      <span aria-hidden='true'><FaStar /></span>
      <strong>{rating.toFixed(1)}</strong>
      <span>{reviews.length} {reviews.length === 1 ? 'review' : 'reviews'}</span>
    </a>
  )
}

const ProductGallery = ({ product }) => {
  const images = useMemo(() => uniqueImages(product), [product])
  const [selected, setSelected] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  useEffect(() => {
    setSelected(0)
    setLightboxOpen(false)
  }, [product?._id])

  useEffect(() => {
    if (!lightboxOpen) return undefined
    const previousOverflow = document.body.style.overflow
    const onKeyDown = event => {
      if (event.key === 'Escape') setLightboxOpen(false)
      if (event.key === 'ArrowLeft') setSelected(value => (value - 1 + images.length) % images.length)
      if (event.key === 'ArrowRight') setSelected(value => (value + 1) % images.length)
    }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [images.length, lightboxOpen])

  if (!images.length) return null

  const selectPrevious = () => setSelected(value => (value - 1 + images.length) % images.length)
  const selectNext = () => setSelected(value => (value + 1) % images.length)

  return (
    <section className={styles.gallery} aria-label={`${product.name} photos`}>
      <button
        type='button'
        className={styles.mainImageButton}
        onClick={() => setLightboxOpen(true)}
        aria-label={`Open enlarged photo ${selected + 1} of ${images.length}`}
      >
        <Media
          picture={images[selected]}
          alt={`${product.name}${selected ? ` at a Miami Sound Rental event, photo ${selected + 1}` : ''}`}
          priority
          sizes='(max-width: 899px) 100vw, 52vw'
          ratio='4 / 3'
          className={styles.mainMedia}
        />
        <span>View larger</span>
      </button>

      {images.length > 1 && <div className={styles.thumbnailRail} aria-label='Choose a product photo'>
        {images.map((picture, index) => (
          <button
            type='button'
            key={`${picture}-${index}`}
            className={`${styles.thumbnail} ${selected === index ? styles.selectedThumbnail : ''}`}
            onClick={() => setSelected(index)}
            aria-label={`Show photo ${index + 1} of ${images.length}`}
            aria-pressed={selected === index}
          >
            <Media
              picture={picture}
              alt=''
              sizes='84px'
              ratio='4 / 3'
              className={styles.thumbnailMedia}
            />
          </button>
        ))}
      </div>}

      {lightboxOpen && <div className={styles.lightbox} role='dialog' aria-modal='true' aria-label={`${product.name} photo viewer`}>
        <button type='button' className={styles.lightboxClose} onClick={() => setLightboxOpen(false)} aria-label='Close photo viewer'>
          <FiX aria-hidden='true' />
        </button>
        {images.length > 1 && <button type='button' className={`${styles.lightboxArrow} ${styles.lightboxPrevious}`} onClick={selectPrevious} aria-label='Previous photo'>
          <FiChevronLeft aria-hidden='true' />
        </button>}
        <Media
          picture={images[selected]}
          alt={`${product.name}, enlarged photo ${selected + 1} of ${images.length}`}
          sizes='95vw'
          ratio='4 / 3'
          className={styles.lightboxMedia}
        />
        {images.length > 1 && <button type='button' className={`${styles.lightboxArrow} ${styles.lightboxNext}`} onClick={selectNext} aria-label='Next photo'>
          <FiChevronRight aria-hidden='true' />
        </button>}
        <span className={styles.lightboxCount}>{selected + 1} / {images.length}</span>
      </div>}
    </section>
  )
}

const IncludedProductsV2 = ({ product }) => {
  const [expanded, setExpanded] = useState(false)
  const included = (product?.included || []).filter(entry => entry?.product)
  if (!included.length) return null

  const visible = expanded ? included : included.slice(0, 4)
  return (
    <section className={styles.included} aria-labelledby='included-equipment-heading'>
      <div className={styles.sectionHeadingRow}>
        <div>
          <span className={styles.eyebrow}>No extra rental charge</span>
          <h2 id='included-equipment-heading'>Included with this product</h2>
        </div>
        <span className={styles.itemCount}>{included.length} {included.length === 1 ? 'item' : 'items'}</span>
      </div>
      <ul id='included-equipment-list' className={styles.includedList}>
        {visible.map((entry, index) => {
          const includedProduct = entry.product
          return <li key={includedProduct?._id || `${includedProduct?.name}-${index}`}>
            <Link href={`/products/${helper.makeUrl(includedProduct?.name)}`} legacyBehavior>
              <a className={styles.includedLink}>
                <Media picture={includedProduct?.picture} alt='' sizes='48px' ratio='1 / 1' className={styles.includedMedia} />
                <span>{includedProduct?.name}</span>
                <strong>{entry.units || 1} {(entry.units || 1) === 1 ? 'unit' : 'units'}</strong>
              </a>
            </Link>
          </li>
        })}
      </ul>
      {included.length > 4 && <Action
        variant='quiet'
        size='compact'
        onClick={() => setExpanded(value => !value)}
        ariaExpanded={expanded}
        ariaControls='included-equipment-list'
      >
        {expanded ? 'Show fewer included items' : `Show all ${included.length} included items`}
      </Action>}
    </section>
  )
}

const RelatedProductCard = ({ product, kind, hasOrder, onChoose, onView }) => {
  const attributes = (product?.snippets || []).map(parseSnippet).filter(Boolean).slice(0, 2)
  return (
    <article className={styles.relatedCard}>
      <Link href={`/products/${helper.makeUrl(product.name)}`} legacyBehavior>
        <a className={styles.relatedMediaLink} onClick={() => onView?.(product)} aria-label={`View ${product.name}`}>
          <Media picture={product.picture} alt={product.name} sizes='(max-width: 599px) 88px, 180px' ratio='4 / 3' className={styles.relatedMedia} />
        </a>
      </Link>
      <div className={styles.relatedCopy}>
        <Link href={`/products/${helper.makeUrl(product.name)}`} legacyBehavior>
          <a className={styles.relatedName} onClick={() => onView?.(product)}>{product.name}</a>
        </Link>
        <strong className={styles.relatedPrice}>{helper.formatPrice(product?.price?.rental)} <span>/ 24h</span></strong>
        {kind === 'recommended'
          ? <p>{relatedReason(product.name)}</p>
          : attributes.length > 0
            ? <ul>{attributes.map(attribute => <li key={`${attribute.label}-${attribute.value}`}><span>{attribute.label}</span><strong>{attribute.value}</strong></li>)}</ul>
            : <p>Compare pricing and product details with your current selection.</p>}
      </div>
      <div className={styles.relatedActions}>
        <Action variant={kind === 'recommended' ? 'primary' : 'outline'} size='compact' onClick={() => onChoose(product)}>
          {hasOrder ? 'Add to order' : 'Rent this item'} <FiArrowRight aria-hidden='true' />
        </Action>
        {kind === 'alternative' && <Action variant='quiet' size='compact' href={`/products/${helper.makeUrl(product.name)}`} onClick={() => onView?.(product)}>View details</Action>}
      </div>
    </article>
  )
}

const RelatedSection = ({ title, description, products, kind, hasOrder, onChoose, onView }) => {
  if (!products?.length) return null
  return (
    <section className={styles.relatedSection} aria-labelledby={`${kind}-heading`}>
      <div className={styles.sectionHeadingRow}>
        <div>
          <h2 id={`${kind}-heading`}>{title}</h2>
          <p>{description}</p>
        </div>
      </div>
      <div className={styles.relatedGrid}>
        {products.map(product => <RelatedProductCard
          key={product._id}
          product={product}
          kind={kind}
          hasOrder={hasOrder}
          onChoose={onChoose}
          onView={onView}
        />)}
      </div>
    </section>
  )
}

const ProductDetailV2 = ({
  product,
  category,
  subCategory,
  categoryHref,
  subCategoryHref,
  orlando,
  order,
  cart,
  workingPackage,
  purchaseQuantity,
  setPurchaseQuantity,
  showRental,
  showPurchase,
  deliveryEstimate,
  deliveryMinimum,
  portfolioProof,
  onOpenRentalStart,
  onPurchase,
  onChooseRelated,
  onViewRelated,
  onAskQuestion,
  onLeaveReview,
  onCall,
  onChat,
  onViewPortfolioProof,
  onUseLegacy
}) => {
  const snippetDetails = (product?.snippets || []).map(parseSnippet).filter(Boolean)
  const [showMobileSticky, setShowMobileSticky] = useState(false)
  const eventCapacity = findEventCapacity(product?.description)
  const cartItem = cart?.items?.find(item => String(item?.product?._id) === String(product?._id))
  const packageItem = workingPackage?.components?.find(item => String(item?.product?._id) === String(product?._id) || item?.product?.name === product?.name)
  const hasOrder = Boolean(order?._id || cart?.items?.length)
  const hasActiveRental = Boolean(order?._id && order?.request_type === 'rental' && !cart?.production)
  const pickupPolicyAllowed = product?.allow_pickup === true
  const pickupAvailable = Boolean(product?.allow_pickup && !product?.requires_tech && (!orlando || product?.orlando))
  const rentalPrice = product?.price?.rental
  const locationName = orlando ? 'Orlando' : 'Miami'
  const address = orlando ? '8600 Commodity Cir #152, Orlando, FL 32819' : '20227 15th Ct, Miami, FL 33179'
  const descriptionId = 'product-about-heading'
  const qna = product?.qna?.filter(value => value.includes('/answer/')) || []
  const wattageSnippet = snippetDetails.find(attribute => attribute.label.toLowerCase() === 'wattage')

  const decisionHighlights = []
  if (eventCapacity?.single) decisionHighlights.push(`Recommended for up to ${eventCapacity.single} people${eventCapacity.pair ? `, or ${eventCapacity.pair} with a pair` : ''}`)
  if (wattageSnippet?.value) decisionHighlights.push(`${wattageSnippet.value} powered speaker`)
  else if (product?.wattage) decisionHighlights.push(`${product.wattage}W power requirement`)
  if (product?.included?.length) decisionHighlights.push(`${product.included.length} included ${product.included.length === 1 ? 'item' : 'items'} at no extra rental charge`)
  if (product?.outdoor_rated) decisionHighlights.push('Designed for outdoor use')
  else if (product?.requires_tech) decisionHighlights.push('Professional delivery and operation required')
  else if (pickupAvailable) decisionHighlights.push('Available for warehouse pickup or delivery & setup')
  else if (!pickupPolicyAllowed) decisionHighlights.push('Delivery & setup required')
  snippetDetails.forEach(attribute => {
    const value = `${attribute.label}: ${attribute.value}`
    if (decisionHighlights.length < 4 && !decisionHighlights.some(existing => existing.toLowerCase().includes(attribute.label.toLowerCase()) || existing.toLowerCase().includes(attribute.value.toLowerCase()))) decisionHighlights.push(value)
  })

  useEffect(() => {
    if (!showRental || workingPackage || cartItem || hasActiveRental) {
      setShowMobileSticky(false)
      return undefined
    }
    const primaryAction = document.getElementById('product-rent-primary')
    if (!primaryAction || typeof IntersectionObserver === 'undefined') return undefined
    const observer = new IntersectionObserver(([entry]) => setShowMobileSticky(!entry.isIntersecting), { threshold: 0.35 })
    observer.observe(primaryAction)
    return () => observer.disconnect()
  }, [cartItem, hasActiveRental, showRental, workingPackage])

  const renderRentalAction = () => {
    if (!showRental) return null
    if (workingPackage) return <Suspense fallback={<span className={styles.loadingControl}>Loading setup controls…</span>}>
      <AddUnits
        bigButton
        ui='v2'
        subCat
        pack
        selectedUnits={packageItem?.units || 0}
        product={product}
      />
    </Suspense>
    if (cartItem || hasActiveRental) return <Suspense fallback={<span className={styles.loadingControl}>Loading rental controls…</span>}>
      <AddUnits bigButton ui='v2' contextName='Cart' selectedUnits={cartItem?.units || 0} product={product} />
    </Suspense>
    return <Action id='product-rent-primary' variant='primary' size='large' className={styles.primaryCta} onClick={() => onOpenRentalStart(product)}>
      Choose dates &amp; rent online <FiArrowRight aria-hidden='true' />
    </Action>
  }

  return (
    <UiBoundary className={styles.page} data-msr-product-page='v2'>
      <div className={styles.shell}>
        <nav className={styles.breadcrumbs} aria-label='Breadcrumb'>
          <ol>
            <li><Link href='/' legacyBehavior><a>Home</a></Link></li>
            <li><Link href={categoryHref} legacyBehavior><a>{category}</a></Link></li>
            <li><Link href={subCategoryHref} legacyBehavior><a>{subCategory}</a></Link></li>
            <li aria-current='page'>{product.name}</li>
          </ol>
        </nav>

        <header className={styles.header}>
          <div>
            <span className={styles.eyebrow}>{subCategory} rental</span>
            <h1>{product.name}</h1>
          </div>
          <RatingSummary reviews={product.reviews} />
        </header>

        <div className={styles.heroGrid}>
          <ProductGallery product={product} />

          <aside className={styles.decisionCard} aria-label={`Rent ${product.name}`}>
            {decisionHighlights.length > 0 && <ul className={styles.highlights}>
              {decisionHighlights.map(highlight => <li key={highlight}><FiCheck aria-hidden='true' /> <span>{highlight}</span></li>)}
            </ul>}

            {!product.orlando && orlando && <div className={styles.locationWarning} role='status'>
              This product is stocked in Miami. A one-time transfer charge may apply to an Orlando order.
            </div>}

            {showRental && <div className={styles.rentalDecision}>
              <span className={styles.priceLabel}>Equipment rental</span>
              <div className={styles.priceRow}>
                <strong>{helper.formatPrice(rentalPrice)}</strong>
                <span>/ 24 hours</span>
              </div>
              <p className={styles.fulfillmentLead}>{pickupPolicyAllowed ? 'Warehouse pickup or delivery & setup' : 'Delivery & setup'}</p>
              {renderRentalAction()}
              <p className={styles.availabilityNote}>
                <FiCalendar aria-hidden='true' />
                {order?._id
                  ? pickupPolicyAllowed
                    ? 'Availability is checked against the dates and warehouse selected for this order.'
                    : 'Delivery availability is checked against the dates and location selected for this order.'
                  : pickupPolicyAllowed
                    ? 'Select dates and location first so we can confirm the correct warehouse and live availability.'
                    : 'Select dates and location first so we can confirm delivery availability and setup pricing.'}
              </p>
            </div>}

            {showPurchase && <div className={`${styles.purchaseDecision} ${showRental ? styles.secondaryPurchaseDecision : ''}`}>
              <div className={styles.purchasePrice}>
                <span className={styles.priceLabel}>Purchase price</span>
                <strong>{helper.formatPrice(product?.price?.map || product?.price?.purchase)}</strong>
              </div>
              <div className={styles.purchaseControls}>
                <label className={styles.purchaseQuantity} htmlFor='product-purchase-quantity'>
                  <span>Quantity</span>
                  <select id='product-purchase-quantity' value={purchaseQuantity} onChange={event => setPurchaseQuantity(Number(event.target.value))}>
                    {Array.from({ length: 20 }, (_, index) => index + 1).map(quantity => <option value={quantity} key={quantity}>{quantity}</option>)}
                  </select>
                </label>
                <Action
                  variant={showRental ? 'outline' : 'primary'}
                  size={showRental ? 'compact' : 'standard'}
                  icon={AiOutlineShoppingCart}
                  disabled={!product.allow_pickup || (!product.orlando && orlando)}
                  onClick={onPurchase}
                >
                  Purchase product
                </Action>
              </div>
            </div>}

            <div className={styles.decisionReassurance}>
              <span><FiCheck aria-hidden='true' /> Get a final price after dates and location</span>
              <span><FiPackage aria-hidden='true' /> Keep adding equipment before review</span>
            </div>
          </aside>
        </div>

        {eventCapacity?.single && <section className={styles.sizingGuide} aria-labelledby='sizing-guide-heading'>
          <div>
            <span className={styles.eyebrow}>Quick sizing guide</span>
            <h2 id='sizing-guide-heading'>How many do you need?</h2>
          </div>
          <div className={styles.sizingOptions}>
            <div><strong>1</strong><span>For events up to {eventCapacity.single} people</span></div>
            {eventCapacity.pair && <div><strong>2</strong><span>For broader coverage up to {eventCapacity.pair} people</span></div>}
          </div>
          <p>These are quick planning guidelines. Venue acoustics, program type, and room shape can change the recommendation.</p>
        </section>}

        <IncludedProductsV2 product={product} />

        {!workingPackage && !product.only_purchase && <section className={styles.fulfillment} aria-labelledby='fulfillment-heading'>
          <div className={styles.sectionHeadingRow}>
            <div>
              <span className={styles.eyebrow}>{pickupPolicyAllowed ? 'Choose after clicking rent online' : 'Professional fulfillment'}</span>
              <h2 id='fulfillment-heading'>{pickupPolicyAllowed ? 'Warehouse pickup or full delivery & setup' : 'Delivery & setup'}</h2>
              <p>{pickupPolicyAllowed
                ? 'Both options start by confirming dates. Delivery also uses the address to select the correct warehouse and calculate labor.'
                : 'Confirm the dates and event address so we can verify availability and calculate delivery, installation, and collection.'}</p>
            </div>
          </div>
          <div className={styles.fulfillmentGrid}>
            {pickupPolicyAllowed && <article className={`${styles.fulfillmentCard} ${!pickupAvailable ? styles.unavailableCard : ''}`}>
              <span className={styles.fulfillmentIcon}><FiHome aria-hidden='true' /></span>
              <div>
                <h3>Warehouse pickup rental</h3>
                <p>Pay the equipment rental price, then pick up and return the equipment at the selected warehouse.</p>
                {pickupAvailable
                  ? <>
                    <strong>{locationName} warehouse</strong>
                    <span>{address}</span>
                    <details>
                      <summary>View pickup hours</summary>
                      <p>Weekdays: pickup 9 AM–5 PM and return by 4 PM. Weekends: pickup and return 9 AM–12 PM.</p>
                    </details>
                  </>
                  : <strong>{product.requires_tech ? 'Professional delivery is required for this product.' : 'Pickup is not available from the selected warehouse.'}</strong>}
              </div>
            </article>}

            <article className={styles.fulfillmentCard}>
              <span className={styles.fulfillmentIcon}><FiTruck aria-hidden='true' /></span>
              <div>
                <h3>Delivery &amp; setup</h3>
                <p>Our crew delivers, installs, and later collects the equipment at the times confirmed with you.</p>
                <strong>Estimated {orlando ? 'Orange County' : 'Miami-Dade'} labor: {helper.formatPrice(deliveryEstimate)}</strong>
                {deliveryMinimum && <span>A {helper.formatPrice(deliveryMinimum)} minimum delivery charge applies.</span>}
                <span>Final labor is calculated from the complete order, dates, access window, and address.</span>
              </div>
            </article>
          </div>
        </section>}

        <section className={styles.about} aria-labelledby={descriptionId}>
          <div className={styles.aboutCopy}>
            <span className={styles.eyebrow}>Product details</span>
            <h2 id={descriptionId}>About the {product.name}</h2>
            <p>{product.description}</p>
          </div>
          {snippetDetails.length > 0 && <dl className={styles.specifications}>
            {snippetDetails.map(attribute => <div key={`${attribute.label}-${attribute.value}`}>
              <dt>{attribute.label}</dt>
              <dd>{attribute.value}</dd>
            </div>)}
          </dl>}
        </section>

        <ProductPortfolioProof
          event={portfolioProof}
          onView={onViewPortfolioProof}
        />

        <section className={styles.help} aria-labelledby='product-help-heading'>
          <div className={styles.helpIcon}><FiMessageCircle aria-hidden='true' /></div>
          <div>
            <h2 id='product-help-heading'>Need help choosing the right equipment?</h2>
            <p>An AV expert can help with quantities, compatibility, room coverage, and delivery planning.</p>
          </div>
          <div className={styles.helpActions}>
            <Action variant='primary' icon={FiPhone} external href={orlando ? 'tel:+14078070052' : 'tel:+13059744411'} onClick={onCall}>Call an AV expert</Action>
            <Action variant='darkSecondary' icon={FiMessageCircle} onClick={onChat}>Chat with us</Action>
          </div>
        </section>

        <RelatedSection
          title='Frequently rented together'
          description='Compatible additions that can make this setup more complete.'
          products={product.recommended_products}
          kind='recommended'
          hasOrder={hasOrder}
          onChoose={onChooseRelated}
          onView={onViewRelated}
        />

        <RelatedSection
          title='Compare similar products'
          description='Review other options before choosing the equipment that best fits your event.'
          products={product.alternatives}
          kind='alternative'
          hasOrder={hasOrder}
          onChoose={onChooseRelated}
          onView={onViewRelated}
        />

        {product.reviews?.length > 0 && <section id='product-reviews' className={styles.reviews} aria-labelledby='reviews-heading'>
          <div className={styles.sectionHeadingRow}>
            <div><h2 id='reviews-heading'>Customer reviews</h2><p>Feedback from customers who used this equipment.</p></div>
            <Action variant='outline' size='compact' onClick={onLeaveReview}>Leave a review</Action>
          </div>
          <div className={styles.reviewGrid}>
            {[...product.reviews].reverse().map((review, index) => <article key={review._id || index}>
              <div><strong>{review?.user?.first_name || 'Customer'}</strong><span>{new Date(review.datePublished).toLocaleDateString()}</span></div>
              <span className={styles.reviewStars} aria-label={`${review.rating} out of 5 stars`}>{Array.from({ length: review.rating }, (_, star) => <FaStar key={star} aria-hidden='true' />)}</span>
              <h3>{review.name}</h3>
              <p>{review.reviewBody}</p>
            </article>)}
          </div>
        </section>}

        <section className={styles.qna} aria-labelledby='qna-heading'>
          <div className={styles.sectionHeadingRow}>
            <div><h2 id='qna-heading'>Questions &amp; answers</h2><p>{pickupPolicyAllowed
              ? 'Quick answers about compatibility, pickup, setup, and use.'
              : 'Quick answers about compatibility, delivery, setup, and use.'}</p></div>
            <Action variant='outline' size='compact' onClick={onAskQuestion}>Ask a question</Action>
          </div>
          {qna.length > 0
            ? <div className={styles.qnaList}>{qna.map((value, index) => {
              const [question, answer] = value.split('/answer/')
              return <details key={`${question}-${index}`}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            })}</div>
            : <div className={styles.noQna}>
              <FiMessageCircle aria-hidden='true' />
              <div><strong>Have a product question?</strong><span>Ask us about compatibility, connections, or what your event needs.</span></div>
            </div>}
        </section>

        {!product.reviews?.length && <section className={styles.trustSection} aria-labelledby='trust-heading'>
          <div><FiShield aria-hidden='true' /><h2 id='trust-heading'>Rent with confidence</h2></div>
          <ul>
            <li><FiCheck aria-hidden='true' /> Real photos of equipment used at events</li>
            <li><FiCheck aria-hidden='true' /> {pickupPolicyAllowed ? 'Dates and warehouse checked before final pricing' : 'Dates and delivery location checked before final pricing'}</li>
            <li><FiCheck aria-hidden='true' /> Online booking with expert help when you need it</li>
          </ul>
        </section>}

        <div className={styles.variantSwitch}>
          <button type='button' onClick={onUseLegacy}>Use classic product page</button>
        </div>
      </div>

      {showMobileSticky && <div className={styles.mobileStickyCta}>
        <div><strong>{helper.formatPrice(rentalPrice)}</strong><span>/ 24h</span></div>
        <Action variant='primary' onClick={() => onOpenRentalStart(product)}>Choose dates</Action>
      </div>}
    </UiBoundary>
  )
}

export default ProductDetailV2
